// File: signup.js
//
// The page shown after a new user signs in with Google for the first
// time. We collect a phone number (optional), check that they accepted
// the Terms of Service, and create their profile in the database.
//
// After this runs successfully, the user is sent to /home.
//
// Sections in this file:
//   - Helpers   (splitting names, etc.)
//   - State     (form values + auth check)
//   - Submit    (handleCreateAccount — writes the new user doc)
//   - Render    (the visible page: header + form + create-account card)

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, provider } from "./firebaseConfig";
import { signInWithPopup } from "firebase/auth";
import {
  getFirestore,
  setDoc,
  doc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";
import { cap10Digits, formatUSPhone } from "./utils/phone";

const db = getFirestore();

// Splits Google's "Full Name" into first + last so we can save them
// separately. "Jane Q. Doe" -> { firstName: "Jane Q.", lastName: "Doe" }.
function splitName(displayName = "") {
  const trimmed = displayName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(" ");
  return { firstName, lastName };
}

function Signup() {
  const navigate = useNavigate();

  // The form values the user fills in.
  const [tosAccepted, setTosAccepted] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [phone, setPhone] = useState("");

  // Status: error to show, "creating..." flag, and whether Google
  // sign-in has finished (we wait for it before letting the form work).
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userReady, setUserReady] = useState(false);

  // When someone arrives here, check if they're already signed in and
  // already have a profile. If so, they shouldn't be on Sign Up — send
  // them to /home instead.
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setUserReady(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          navigate("/home", { replace: true });
          return;
        }

        setUserReady(true);
      } catch (e) {
        console.error("Signup auth check failed:", e);
        setErrorMessage("Could not verify account. Please try again.");
      }
    });

    return () => unsub();
  }, [navigate]);

  // Make sure we have a Google-signed-in user. If they aren't signed
  // in yet, pop up the Google sign-in window.
  const ensureGoogleUser = async () => {
    if (auth.currentUser) return auth.currentUser;
    const result = await signInWithPopup(auth, provider);
    return result.user;
  };

  // Runs when the "Create Account" button is clicked. Validates the
  // form, then writes a new user document to Firestore. The shape of
  // the data saved below defines what each user's profile looks like.
  const handleCreateAccount = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (!tosAccepted) {
        setErrorMessage("Please accept the Terms of Service to continue.");
        return;
      }

      const phoneDigits = cap10Digits(phone);
      if (phone.trim() && phoneDigits.length !== 10) {
        setErrorMessage("Please enter a valid 10-digit phone number or leave it blank.");
        return;
      }

      const user = await ensureGoogleUser();

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      if (snap.exists()) {
        navigate("/home");
        return;
      }

      const { firstName, lastName } = splitName(user.displayName || "");

      await setDoc(
        userRef,
        {
          uid: user.uid,
          email: user.email || "",
          name: user.displayName || "",
          firstName,
          lastName,

          approved: false,
          staff: false,
          management: false,

          staffStatus: "offline",
          lastActiveAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),

          newsletterOptIn: newsletter,
          tosAccepted: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),

          ...(phoneDigits ? { phoneNumber: phoneDigits } : {}),
        },
        { merge: true }
      );

      navigate("/home");
    } catch (error) {
      console.error("Signup create error:", error);

      const code = error?.code || "";
      if (code === "auth/unauthorized-domain") {
        setErrorMessage("This domain isn't authorized in Firebase yet (unauthorized-domain).");
      } else if (code === "auth/popup-blocked") {
        setErrorMessage("Popup was blocked by the browser. Allow popups and try again.");
      } else {
        setErrorMessage("Error creating account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 relative flex flex-col" style={pageBackground}>
      {/* --- Page header (logo + "Sign up" label + Cancel button) --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to landing page"
            type="button"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Sign up
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate("/")}
            className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            type="button"
          >
            Cancel
          </button>
        </div>
      </header>

      {/* --- Main content: two-column layout. Left side is the form
              fields (phone, TOS, newsletter). Right side is the
              "Create account" card with the submit button. On mobile,
              the right card stacks below the left one. --- */}
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
          <section
            className="backdrop-blur border rounded-3xl p-5 sm:p-6 lg:p-8"
            style={{
              backgroundColor: THEME.cardBg,
              borderColor: THEME.border,
              boxShadow: THEME.shadow,
            }}
          >
            <div
              className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
              style={{ color: COLORS.brown }}
            >
              Welcome
            </div>

            <h1
              className="text-3xl sm:text-4xl font-extrabold mt-4 leading-tight"
              style={{ color: THEME.text }}
            >
              Finish setting up your account.
            </h1>

            <p
              className="mt-3 max-w-2xl text-base leading-7"
              style={{ color: "rgba(31,56,0,0.68)" }}
            >
              We just need a couple of details before you can start using HOPE
              Navigator.
            </p>

            <div className="mt-6 space-y-5">
              <div>
                <label
                  className="block text-sm font-extrabold mb-2"
                  style={{ color: THEME.text }}
                  htmlFor="signup-phone"
                >
                  Cell phone (optional)
                </label>

                <input
                  id="signup-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="(555) 123-4567"
                  value={formatUSPhone(phone)}
                  onChange={(e) => setPhone(cap10Digits(e.target.value))}
                  className="w-full rounded-2xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 focus:ring-white/80"
                  style={{
                    borderColor: THEME.border,
                    color: THEME.text,
                  }}
                />

                <p
                  className="text-xs mt-1"
                  style={{ color: "rgba(31,56,0,0.55)" }}
                >
                  10 digits max.
                </p>
              </div>

              <label
                className="flex items-start gap-3 cursor-pointer"
                style={{ color: THEME.text }}
              >
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={() => setTosAccepted(!tosAccepted)}
                  className="mt-1 w-4 h-4 accent-[color:var(--accent)]"
                  style={{ accentColor: COLORS.greenDark }}
                />
                <span className="text-sm">
                  I accept the{" "}
                  <a
                    href="/tos"
                    className="font-extrabold underline decoration-black/20 hover:decoration-black/40"
                    style={{ color: COLORS.aquaDark }}
                  >
                    Terms of Service
                  </a>
                </span>
              </label>

              <label
                className="flex items-start gap-3 cursor-pointer"
                style={{ color: THEME.text }}
              >
                <input
                  type="checkbox"
                  checked={newsletter}
                  onChange={() => setNewsletter(!newsletter)}
                  className="mt-1 w-4 h-4"
                  style={{ accentColor: COLORS.greenDark }}
                />
                <span className="text-sm">Subscribe to our newsletter</span>
              </label>
            </div>

            {!userReady && (
              <div
                className="mt-5 text-xs leading-relaxed rounded-2xl border px-4 py-3"
                style={{
                  color: "rgba(31,56,0,0.62)",
                  backgroundColor: COLORS.greenLight,
                  borderColor: THEME.border,
                }}
              >
                If you haven't signed in yet, we'll ask you to continue with
                Google when you create the account.
              </div>
            )}
          </section>

          <aside className="lg:sticky lg:top-[72px]">
            <div
              className="rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur relative overflow-hidden"
              style={{
                backgroundColor: THEME.cardBg,
                borderColor: THEME.border,
                boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
              }}
            >
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ backgroundColor: THEME.primary }}
              />

              <div
                className="text-sm font-extrabold uppercase tracking-wide"
                style={{ color: COLORS.aquaDark }}
              >
                Final step
              </div>

              <div
                className="text-2xl font-extrabold mt-2"
                style={{ color: THEME.text }}
              >
                Create account
              </div>

              <div
                className="text-sm mt-2 leading-6"
                style={{ color: "rgba(31,56,0,0.66)" }}
              >
                Once you accept the Terms of Service, finish creating your
                H.O.P.E Navigator account below.
              </div>

              <button
                onClick={handleCreateAccount}
                disabled={isLoading || !tosAccepted}
                className="mt-5 w-full rounded-xl px-4 py-3 text-base font-extrabold text-white transition hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-4"
                style={{
                  backgroundColor: COLORS.greenBase,
                  boxShadow: "0 10px 20px rgba(64,115,0,0.18)",
                }}
                type="button"
              >
                {isLoading ? "Creating..." : "Create Account"}
              </button>

              {errorMessage && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {errorMessage}
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default Signup;
